import { useState, useCallback, useEffect, useRef } from 'react';
import { Step, CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import { completeTour as completeTourAction, resetTourCompletion } from '../store/actions/TourActions';
import { GUIDANCE_DISABLED_KEY, isGuidancePausedLocal, isTourForceLocal } from '../utils/tourGuidance';

const STORAGE_PREFIX = 'pons_tour_';

// Global lock so only one tour runs at a time
let activeTourId: string | null = null;

/** Force-clear the global tour lock - call on page transitions to prevent stale locks. */
export function clearActiveTourLock(): void {
  activeTourId = null;
}

export interface UseFeatureTourOptions {
  /** Unique identifier for this tour - used as storage key */
  tourId: string;
  /** Joyride step definitions */
  steps: Step[];
  /** Only show when this is true (e.g. data is loaded, empty state visible) */
  readyToShow?: boolean;
  /** Delay in ms before auto-starting the tour (default: 600) */
  autoStartDelay?: number;
  /** Callback when the tour finishes (completed or skipped) */
  onComplete?: () => void;
}

export interface UseFeatureTourReturn {
  /** Whether this tour has already been completed */
  hasCompleted: boolean;
  /** Whether the tour is currently running */
  isRunning: boolean;
  /** The Joyride steps */
  steps: Step[];
  /** Start the tour manually */
  startTour: () => void;
  /** Stop the tour and mark as completed */
  dismissTour: () => void;
  /** Reset tour so it shows again (for "replay" functionality) */
  resetTour: () => void;
  /** Joyride callback - pass directly to <FeatureTour onCallback={handleJoyrideCallback} /> */
  handleJoyrideCallback: (data: CallBackProps) => void;
}

function getStorageKey(tourId: string): string {
  return `${STORAGE_PREFIX}${tourId}`;
}

/** Check localStorage as a fast cache (used before Redux is loaded). */
function isTourCompletedLocal(tourId: string): boolean {
  try {
    return localStorage.getItem(getStorageKey(tourId)) === 'completed';
  } catch {
    return false;
  }
}

function markTourCompletedLocal(tourId: string): void {
  try {
    localStorage.setItem(getStorageKey(tourId), 'completed');
  } catch {
    // localStorage may be unavailable
  }
}

function clearTourCompletedLocal(tourId: string): void {
  try {
    localStorage.removeItem(getStorageKey(tourId));
  } catch {
    // localStorage may be unavailable
  }
}

export function useFeatureTour({
  tourId,
  steps,
  readyToShow = true,
  autoStartDelay = 600,
  onComplete,
}: UseFeatureTourOptions): UseFeatureTourReturn {
  const dispatch = useAppDispatch();
  const tourLoaded = useSelector((state: RootState) => state.tour.loaded);
  const reduxCompleted = useSelector((state: RootState) => !!state.tour.completions[tourId]);
  const guidanceDisabledRedux = useSelector((state: RootState) => !!state.tour.completions[GUIDANCE_DISABLED_KEY]);

  // Use Redux as source of truth once loaded, fall back to localStorage.
  const completedByState = tourLoaded ? reduxCompleted : isTourCompletedLocal(tourId);
  // Dev "force tours" flag overrides completion so already-seen tours re-appear.
  const isCompleted = isTourForceLocal() ? false : completedByState;
  // Global "guidance paused" preference - when set, nothing auto-starts.
  const guidancePaused = tourLoaded ? guidanceDisabledRedux : isGuidancePausedLocal();

  const [hasCompleted, setHasCompleted] = useState(isCompleted);
  const [isRunning, setIsRunning] = useState(false);
  const autoStartedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const prevTourIdRef = useRef(tourId);

  // Sync hasCompleted when Redux loads or completions change
  useEffect(() => {
    setHasCompleted(isCompleted);
  }, [isCompleted]);

  // When tourId changes (e.g. wizard step change), stop the previous tour and reset
  useEffect(() => {
    if (prevTourIdRef.current !== tourId) {
      if (activeTourId === prevTourIdRef.current) {
        activeTourId = null;
      }
      setIsRunning(false);
      autoStartedRef.current = false;
      prevTourIdRef.current = tourId;
    }
  }, [tourId]);

  // Auto-start on first visit when conditions are met.
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  useEffect(() => {
    if (guidancePaused || hasCompleted || autoStartedRef.current || !readyToShow || steps.length === 0) {
      return;
    }

    const pollMs = 300;
    const maxMs = 8000;
    let elapsed = 0;

    const interval = setInterval(() => {
      elapsed += pollMs;

      if (autoStartedRef.current || elapsed > maxMs) {
        clearInterval(interval);
        return;
      }

      if (elapsed < autoStartDelay) return;
      if (activeTourId) return;

      const firstTarget = stepsRef.current[0]?.target;
      if (typeof firstTarget === 'string' && !document.querySelector(firstTarget)) return;

      clearInterval(interval);
      autoStartedRef.current = true;
      activeTourId = tourId;
      setIsRunning(true);
    }, pollMs);

    return () => clearInterval(interval);
  }, [guidancePaused, hasCompleted, readyToShow, steps.length, autoStartDelay, tourId]);

  // Release global lock when this component unmounts while running
  useEffect(() => {
    return () => {
      if (activeTourId === tourId) {
        activeTourId = null;
      }
    };
  }, [tourId]);

  const startTour = useCallback(() => {
    if (activeTourId && activeTourId !== tourId) return;
    activeTourId = tourId;
    setIsRunning(true);
  }, [tourId]);

  const dismissTour = useCallback(() => {
    setIsRunning(false);
    setHasCompleted(true);
    markTourCompletedLocal(tourId);
    dispatch(completeTourAction(tourId));
    if (activeTourId === tourId) {
      activeTourId = null;
    }
    onCompleteRef.current?.();
  }, [tourId, dispatch]);

  const resetTour = useCallback(() => {
    clearTourCompletedLocal(tourId);
    dispatch(resetTourCompletion(tourId));
    setHasCompleted(false);
    autoStartedRef.current = false;
  }, [tourId, dispatch]);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, type, action } = data;
      const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

      if (finishedStatuses.includes(status)) {
        dismissTour();
        return;
      }

      // Handle close button click on individual steps
      if (type === EVENTS.STEP_AFTER && action === ACTIONS.CLOSE) {
        dismissTour();
      }
    },
    [dismissTour],
  );

  return {
    hasCompleted,
    isRunning,
    steps,
    startTour,
    dismissTour,
    resetTour,
    handleJoyrideCallback,
  };
}

/** Current global guidance-paused state (Redux once loaded, else localStorage). */
export function useGuidancePaused(): boolean {
  const tourLoaded = useSelector((state: RootState) => state.tour.loaded);
  const disabledRedux = useSelector((state: RootState) => !!state.tour.completions[GUIDANCE_DISABLED_KEY]);
  return tourLoaded ? disabledRedux : isGuidancePausedLocal();
}
